import React, { useEffect, useState } from 'react';
import { FiUsers } from 'react-icons/fi';
import LiveClassChat from './LiveClassChat';
import { useClient } from 'jsonapi-react';
import toast from 'react-hot-toast';
import ActionCableConsumer from '@/utils/actionCableConsumer';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '@/components/custom/empty-state';
import NPSModal from './NPSModal';
import { YoutubeWithSurvey } from '@/components/custom/youtube-with-survey';
import { VimeoWithSurvey } from '@/components/custom/vimeo-with-survey';
import { useCheckIncludeProfiles } from '@/hooks/useAuthorityLevel';
import FullScreenModal from './fullScreenModal';
import { DashboardContent } from '@/layouts/dashboard';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageContent, Page, PageSidebar } from '@/components/custom/page';
import { LiveClassPlayer, LiveClassPlayerEmbed } from '@/components/custom/live-class';
// import { LiveClassPlayerOverlay, LiveClassPlayerOverlayBody, LiveClassPlayerOverlayInner, LiveClassPlayerOverlayTitle } from '@/components/custom/live-class';
// import { Link } from 'react-router-dom';
import { Loader } from '@/components/custom/loader';

export default function ViewLiveClass(props) {
  const { uri } = props;

  const client = useClient();
  const { t } = useTranslation();
  const isStudent = useCheckIncludeProfiles({ only: ['student'] });
  const [classData, setClassData] = useState(null);
  const [messageChannel, setMessageChannel] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentsMeta, setCommentsMeta] = useState(null);
  const [videoId, setVideoId] = useState();
  const [videoPlatform, setVideoPlatform] = useState(null);
  const [hasMoreComments, setHasMoreComments] = useState(true);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [answers, setAnswers] = useState([]);
  const [usersCount, setUsersCount] = useState(0);
  const [nextClassStarted, setNextClassStarted] = useState(false);
  const [intervalId, setIntervalId] = useState();
  const [isOnSchedule, setIsOnSchedule] = useState(false);
  const [timeLeft, setTimeLeft] = useState(300);
  const [fullScreen, setFullScreen] = useState(false);

  useEffect(() => {
    setClassData(null);
    setMessageChannel(null);
    setComments([]);
    setVideoId(null);
    setVideoPlatform(null);
    setHasMoreComments(true);
    setPage(1);
    setShowModal(false);
    setAnswers([]);
    setUsersCount(0);
    setNextClassStarted(false);
    setTimeLeft(300);
    clearInterval(intervalId);

    // setIntervalId(setInterval(checkNextClassTime, 1000));
    async function loadData() {
      await Promise.allSettled([fetchLiveClassById(), fetchComments(), fetchSurveyIsAnswered()]).finally(() => {
        setIsLoading(false);
      });
    }

    loadData();
  }, [props.liveClassId]);

  const fetchLiveClassById = async () => {
    try {
      const response = await client.fetch(`/live_classrooms/${props.liveClassId}`);

      setClassData(response.data);
    } catch (error) {
      console.error(error);
      toast.error('Erro');
    }
  };

  const fetchSurveyIsAnswered = async () => {
    const { data, error } = await client.fetch(`live_classroom_answers?filter[live_classroom_id]=${props.liveClassId}`);

    if (error) return;

    if (data.length < 5) {
      setAnswers(data);
    }
  };

  async function fetchComments() {
    try {
      const url = `/live_classrooms/${props.liveClassId}/live_classroom_messages?page[number]=${page}&page[size]=20&order=created_at desc`;
      const { data, error, meta } = await client.fetch(url);

      setCommentsMeta(meta);

      if (error || data?.length === 0) {
        setHasMoreComments(false);
        return;
      }

      if (data.length < 20) {
        setHasMoreComments(false);
      }

      if (comments) {
        setComments(prevState => [...data, ...prevState]);
      } else {
        setComments(data);
      }
    } catch (error) {
      console.error(error);
      setHasMoreComments(false);
      toast.error('Erro');
    }
  }

  const checkVideoPlayer = url => {
    const youtubeRegex = /^((?:https?:)?\/\/)?((?:www|m)\.)?((?:youtube(-nocookie)?\.com|youtu.be))(\/(?:[\w\-]+\?v=|embed\/|v\/)?)([\w\-]+)(\S+)?$/;
    const vimeoRegex = /(http|https)?:\/\/(www\.|player\.)?vimeo\.com\/(?:channels\/(?:\w+\/)?|groups\/([^\/]*)\/videos\/|video\/|)(\d+)(?:|\/\?)/;

    const youtubeResult = url.match(youtubeRegex);
    const vimeoResult = url.match(vimeoRegex);
    setVideoPlatform(null);
    if (youtubeResult) {
      setTimeout(() => setVideoPlatform('youtube'), 500);
      setVideoId(youtubeResult[6]);
      return;
    } else if (vimeoResult) {
      setTimeout(() => setVideoPlatform('vimeo'), 500);
      setVideoId(vimeoResult[4]);
    } else {
      setTimeout(() => setVideoPlatform('iframe'), 500);
      setVideoId(url);
    }
  };

  useEffect(() => {
    if (messageChannel) {
      if (messageChannel?.count) {
        setUsersCount(messageChannel.count);
      } else {
        let parsedMessage;

        try {
          parsedMessage = JSON.parse(messageChannel.data);
        } catch (error) {
          return;
        }
        if (parsedMessage.count !== undefined) {
          const count = parsedMessage.count;
        } else if (messageChannel?.action === 'created' && parsedMessage.data && parsedMessage.data.type === 'live-classroom-message') {
          const newMessage = parsedMessage.data.attributes;

          if (comments.length < 50) {
            setComments(prevState => [...prevState, newMessage]);
          } else {
            const lastFourtyNineComments = comments.slice(1, 50);
            setComments([...lastFourtyNineComments, newMessage]);
          }
        } else if (messageChannel?.action === 'deleted') {
          const messageIdToDelete = parsedMessage?.data?.attributes?.id;
          setComments(prevState => prevState.filter(comment => comment.id !== messageIdToDelete));
        }
      }
    }
  }, [messageChannel]);

  useEffect(() => {
    if (page !== 1) {
      fetchComments();
    }
  }, [page]);

  useEffect(() => {
    if (classData?.url) {
      checkVideoPlayer(classData.url);
    }
  }, [classData]);

  const checkNextClassTime = () => {
    const currentDate = moment();
    setIsOnSchedule(currentDate.isBetween(classData?.['initial-date'], classData?.['end-date']));
    const teste = new Date() >= new Date(classData?.['next-live-classroom']?.['initial-date']);
    setNextClassStarted(teste);
    if (timeLeft >= 0 && teste) {
      setTimeLeft(prevTimeLeft => prevTimeLeft - 1);
      // setTimeLeft(timeLeft - 1);
    }
  };

  useEffect(() => {
    checkNextClassTime();
    if (classData) {
      setIntervalId(setInterval(checkNextClassTime, 1000));
      return () => clearInterval(intervalId);
    }
  }, [classData]);

  if (isLoading) {
    return (
      <DashboardContent>
        <Loader />
      </DashboardContent>
    );
  }

  if (!isOnSchedule && !isLoading) {
    return (
      <DashboardContent>
        <EmptyState
          type="content"
          title={t('emptyState.contentNotAvaliable')}
          text={t('emptyState.contactATeacherText')}
          className="m-auto"
        />
      </DashboardContent>
    );
  }

  const breadCrumbs = {
    title: 'Cursos e Aulas',
    nav: [
      {
        route: '/aulas-ao-vivo',
        name: 'Aulas ao vivo',
        isActive: false
      },
      {
        route: uri,
        name: classData?.title,
        isActive: true
      }
    ]
  };

  return (
    <DashboardContent breadcrumbs={breadCrumbs}>
      {classData && (
        <ActionCableConsumer
          channel="LiveClassroomMessageChannel"
          setObjectChannel={setMessageChannel}
          channelKey={'live_classroom_id'}
          channelId={classData?.id}
        />
      )}

      <Page variant="fill">
        <PageContent>
          <div className="flex items-center gap-2">
            <Badge
              variant="success"
              size="xs"
            >
              Online
            </Badge>
            {usersCount > 0 && (
              <Badge
                variant="more"
                size="xs"
                className="cursor-default gap-1"
              >
                <FiUsers /> {usersCount} {t('textsCommon.watchingNow')}
              </Badge>
            )}
          </div>

          <LiveClassPlayer className="flex-grow">
            {videoPlatform !== 'youtube' && (
              <Button
                size="sm"
                wide
                onClick={() => setFullScreen(!fullScreen)}
                className="mb-4 ml-auto"
              >
                Assistir em tela cheia
              </Button>
            )}

            <LiveClassPlayerEmbed>
              {videoPlatform && videoPlatform === 'youtube' && (
                <YoutubeWithSurvey
                  showModalSurvey={showModal}
                  setShowModalSurvey={setShowModal}
                  videoId={videoId}
                  answers={answers}
                />
              )}
              {videoPlatform && videoPlatform === 'vimeo' && (
                <VimeoWithSurvey
                  videoId={videoId}
                  setShowModalSurvey={setShowModal}
                  showModalSurvey={showModal}
                />
              )}
              {timeLeft >= 0 && videoPlatform && videoPlatform === 'iframe' && !fullScreen && (
                <iframe
                  src={videoId}
                  scrolling="no"
                />
              )}
              {/* {nextClassStarted && (
                <LiveClassPlayerOverlay hasNoise={timeLeft <= 0}>
                  <LiveClassPlayerOverlayInner>
                    <LiveClassPlayerOverlayTitle>Sua próxima aula já começou</LiveClassPlayerOverlayTitle>

                    <LiveClassPlayerOverlayBody>{timeLeft >= 0 ? `Esta transmissão será encerrada em ${moment().startOf('day').seconds(timeLeft).format('mm:ss')}` : 'Clique no botão abaixo para assistir'}</LiveClassPlayerOverlayBody>

                    <Button
                      variant="outline"
                      size="sm"
                      wide
                      asChild
                    >
                      <Link to={`/aulas-ao-vivo/${classData?.['next-live-classroom']?.id}`}>Próxima aula</Link>
                    </Button>
                  </LiveClassPlayerOverlayInner>
                </LiveClassPlayerOverlay>
              )} */}
            </LiveClassPlayerEmbed>
          </LiveClassPlayer>
        </PageContent>

        <PageSidebar
          variant="chat"
          overflow={false}
        >
          <LiveClassChat
            comments={comments}
            setComments={setComments}
            hasMoreComments={hasMoreComments}
            setPage={setPage}
            meta={commentsMeta}
            setMeta={setCommentsMeta}
          />
        </PageSidebar>
      </Page>

      {showModal && answers.length < 5 && isStudent && (
        <NPSModal
          answers={answers}
          setAnswers={setAnswers}
        />
      )}
      <FullScreenModal
        visible={fullScreen}
        onClose={() => setFullScreen(false)}
        videoPlatform={videoPlatform}
        timeLeft={timeLeft}
        nextClassStarted={nextClassStarted}
        videoId={videoId}
        classData={classData}
        showModal={showModal}
        setShowModal={setShowModal}
        answers={answers}
      />
    </DashboardContent>
  );
}
